import type { SelectOption } from "@msys/ui";
import {
  Cancel as CancelIcon,
  Close as CloseIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  SelectChangeEvent,
  SelectProps as MuiSelectProps,
  Stack,
  Tooltip,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useField } from "formik";
import { isArray, isEqual, isString, noop, without } from "lodash-es";
import React from "react";
import { Capabilities } from "../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../auth/RestrictedByCapability.js";

interface Props<Value> extends Omit<
  MuiSelectProps<Value[]>,
  "value" | "multiple" | "onChange"
> {
  name: string;
  options?: (SelectOption<Value> & { capability?: Capabilities })[];
  clearable?: boolean;
  onClear?: () => void;
  onChange?: (value: Value[]) => void | Promise<void>;
  value?: Value[] | null;
}

export const SelectMultipleField = <Value,>({
  name,
  options = [],
  clearable,
  fullWidth,
  onClear,
  onChange: onChangeProp,
  value: valueProp,
  onBlur: onBlurProp,
  ...props
}: Props<Value>) => {
  const ref = React.useRef<HTMLElement>(null);
  const { t } = useTranslate(["Global"]);
  const [{ value, onBlur }, { touched, error }, { setValue, setTouched }] =
    useField<Value[] | null>(name);

  const labelId = `${name}-label`;

  const onChange = async (value: Value[]) => {
    if (onChangeProp) {
      await onChangeProp(value);
    } else {
      await setValue(value);
      requestAnimationFrame(() => {
        setTouched(true);
      });
    }
  };
  const currentValue = (valueProp !== undefined ? valueProp : value) ?? [];

  return (
    <FormControl
      disabled={props.disabled}
      error={Boolean(error)}
      fullWidth={fullWidth}
      sx={{
        ...(props.size === "extra-small" && {
          "& .MuiInputLabel-root": { display: "none !important" },
        }),
      }}
    >
      <InputLabel id={labelId}>{props.label}</InputLabel>
      <MuiSelect
        name={name}
        value={currentValue}
        renderValue={selected => {
          if (!selected.length) return null;
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "baseline",
                flexWrap: "wrap",
                ml: -0.5,
                mt: -0.5,
              }}
            >
              {selected.map((v, index) => (
                <Chip
                  key={index}
                  size="small"
                  clickable={false}
                  variant="outlined"
                  label={
                    options?.find(option => isEqual(option.value, v))?.label
                  }
                  onDelete={
                    !props.disabled
                      ? async () => {
                          await onChange(without(currentValue, v));
                        }
                      : undefined
                  }
                  deleteIcon={
                    <CancelIcon
                      onMouseDown={e => {
                        // prevent Select intercepting 'mousedown' event
                        e.stopPropagation();
                        e.preventDefault();
                        ref.current?.focus();
                      }}
                    />
                  }
                  sx={{ ml: 0.5, mt: 0.25 }}
                />
              ))}
            </Box>
          );
        }}
        onChange={async e => {
          await onChange(e.target.value as Value[]);
        }}
        onBlur={e => {
          onBlurProp?.(e);
          onBlur(e);
        }}
        multiple={true}
        fullWidth={fullWidth}
        onClose={noop}
        inputProps={{
          name,
          id: name,
        }}
        IconComponent={
          clearable && currentValue.length > 0 ? EmptyIcon : undefined
        }
        endAdornment={
          clearable && currentValue.length > 0 ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                color="primary"
                onClick={
                  onClear ??
                  (async () => {
                    await onChange([]);
                  })
                }
                aria-label="Clear"
                style={{ position: "relative", top: 8 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
        //makes options appear under text field
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          // getContentAnchorEl: null,
        }}
        sx={{
          "&.MuiInputBase-adornedEnd > .MuiInputBase-input": {
            paddingRight: "0 !important",
          },
        }}
        // error handling for extra-small size
        {...(props.size === "extra-small" && error
          ? {
              startAdornment: (
                <Tooltip title={error}>
                  <ErrorIcon fontSize="small" color="error" />
                </Tooltip>
              ),
              error: true,
              helperText: null,
            }
          : undefined)}
        {...props}
      >
        {options.map(option => {
          const key = option.key ?? JSON.stringify(option.value);
          const field = (
            // @ts-ignore MenuItem only allows number | string | readonly string[] https://github.com/mui-org/material-ui/issues/14286
            <MenuItem
              key={key}
              value={option.value}
              style={{ minHeight: "36px" }}
            >
              {option.icon ? (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  {option.icon}
                  <div>{option.label}</div>
                </Stack>
              ) : (
                option.label
              )}
            </MenuItem>
          );
          if (option.capability) {
            return (
              <RestrictedByCapabilityWithDebug
                key={key}
                capability={option.capability}
              >
                {field}
              </RestrictedByCapabilityWithDebug>
            );
          }
          return field;
        })}
        {!options || !options.length ? (
          <MenuItem disabled style={{ minHeight: "36px" }}>
            {t("No options")}
          </MenuItem>
        ) : null}
      </MuiSelect>
      {touched && error && isString(error) ? (
        <FormHelperText>{error}</FormHelperText>
      ) : null}
    </FormControl>
  );
};
const EmptyIcon = () => <></>;

export const SelectMultipleNumberField = (props: Props<number>) => {
  const [, , { setValue }] = useField<number[]>(props.name);

  return (
    <SelectMultipleField
      onChange={async value => {
        await setValue(
          value.map(value =>
            typeof value === "number" ? value : parseFloat(value as string)
          )
        );
      }}
      onClear={() => setValue([])}
      {...props}
    />
  );
};
